import { Fragment, useEffect, useMemo, useState } from 'react';

import { useSftpConnection, useSftpSettings } from '../../hooks/use-sftp';
import { useSshKeyCounts } from '../../hooks/use-ssh-keys';
import { useTenants } from '../../hooks/use-tenants';
import { useUsers } from '../../hooks/use-users';
import { describeApiError } from '../../lib/api-client';
import { useAuth } from '../../providers/auth-provider';
import { Empty, Pill, Tabs, Td, Th, fmtWhen, inputStyle, mono, primaryButton, secondaryButton, tableStyle, theadRowStyle } from '../proxy-vault/ui';

import { SshKeyList } from './SshKeyList';

import type { SftpAccessReason, SftpConnectionResponse, SftpSettingsBody, TenantUserResponse } from '../../lib/api-client';

/** Console help — the "What it is" paragraph of the plan's user-facing docs (Appendix M), condensed. */
export const SFTP_HELP =
  'Your shiftagent account is your SFTP account: sign in with your email as the username and an SSH key you upload ' +
  'here, and you see the workspace folders of your own organization and every organization beneath it — nothing else. ' +
  'Keys are never generated for you; only the public half is uploaded.';

type TabId = 'keys' | 'accounts' | 'allowlist' | 'connection';

const ACCESS_REASON: Record<SftpAccessReason, string> = {
  no_capability: '✗ your role does not carry SFTP access — an administrator grants it under Roles → SFTP access',
  sftp_disabled: '✗ SFTP is switched off for this environment (Allowlist → Enabled)',
  no_keys: '✗ upload a public key under My keys first',
};

/**
 * SFTP pane — My keys · Accounts · Allowlist · Connection. Everyone whose role carries
 * `sftp_access` sees My keys + Connection; Accounts needs manage_users (or full_access);
 * Allowlist needs full_access and hides when the server refuses the settings (403).
 */
export function SftpView() {
  const { user } = useAuth();
  const caps = user?.capabilities ?? {};
  const isAdmin = caps.full_access === true;
  const canManageUsers = isAdmin || caps.manage_users === true;

  const connection = useSftpConnection();
  const settings = useSftpSettings({ enabled: isAdmin });
  const showAllowlist = isAdmin && !settings.forbidden;

  const tabs = useMemo(
    () => [
      { id: 'keys', label: 'My keys' },
      ...(canManageUsers ? [{ id: 'accounts', label: 'Accounts' }] : []),
      ...(showAllowlist ? [{ id: 'allowlist', label: 'Allowlist' }] : []),
      { id: 'connection', label: 'Connection' },
    ],
    [canManageUsers, showAllowlist],
  );
  const [page, setPage] = useState<TabId>('keys');
  useEffect(() => {
    if (!tabs.some((t) => t.id === page)) setPage('keys');
  }, [tabs, page]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: 'var(--bg)' }}>
      <div
        style={{
          padding: '12px 20px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flexShrink: 0,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>SFTP</div>
          <div data-testid="sftp-help" style={{ fontSize: 11, color: 'var(--text-soft)', lineHeight: 1.5, marginTop: 2 }}>
            {SFTP_HELP}
          </div>
        </div>
        <ServerBadge connection={connection.connection} />
      </div>

      <Tabs tabs={tabs} active={page} onChange={(id) => setPage(id as TabId)} />

      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        {page === 'keys' && user && (
          <MyKeysPage userId={user.id} email={user.email} connection={connection.connection} onChanged={() => void connection.reload()} />
        )}
        {page === 'accounts' && canManageUsers && <AccountsPage />}
        {page === 'allowlist' && showAllowlist && <AllowlistPage settings={settings} onSaved={() => void connection.reload()} />}
        {page === 'connection' && <ConnectionPage conn={connection} />}
      </div>
    </div>
  );
}

// ── header badge ──────────────────────────────────────────────────────────────

function ServerBadge({ connection }: { connection: SftpConnectionResponse | null }) {
  if (!connection) return null;
  const s = connection.server;
  const color = s.status === 'online' ? '#2f855a' : s.status === 'offline' ? 'var(--kind-failure)' : 'var(--text-mute)';
  return (
    <div
      data-testid="sftp-server-badge"
      title={s.last_heartbeat_at ? `last heartbeat ${fmtWhen(s.last_heartbeat_at)}` : 'no heartbeat received yet'}
      style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-soft)', whiteSpace: 'nowrap' }}
    >
      <span style={{ width: 8, height: 8, borderRadius: 999, background: color, display: 'inline-block' }} />
      Server: {s.status}
      {s.version ? ` · v${s.version}` : ''}
      {s.status === 'online' ? ` · ${s.active_sessions} session${s.active_sessions === 1 ? '' : 's'}` : ''}
    </div>
  );
}

// ── My keys ───────────────────────────────────────────────────────────────────

function MyKeysPage({
  userId,
  email,
  connection,
  onChanged,
}: {
  userId: string;
  email: string;
  connection: SftpConnectionResponse | null;
  /** A key was added or revoked — the access verdict on the Connection card depends on it. */
  onChanged: () => void;
}) {
  const rolePermits = connection ? connection.access.permitted || connection.access.reason !== 'no_capability' : null;
  return (
    <div>
      {rolePermits !== null && (
        <div data-testid="sftp-role-permits" style={{ fontSize: 11, color: 'var(--text-soft)', marginBottom: 12 }}>
          {rolePermits
            ? '✓ your role carries SFTP access — a key below lets you log in.'
            : ACCESS_REASON.no_capability}
        </div>
      )}
      <SshKeyList userId={userId} userLabel={email} canManage variant="table" onChanged={onChanged} />
    </div>
  );
}

// ── Accounts ──────────────────────────────────────────────────────────────────

function AccountsPage() {
  const { users, status, error, filters, setFilters } = useUsers();
  const { tenants } = useTenants();
  // The Team page defaults to the home tenant; SFTP accounts span the whole subtree the
  // actor administers, so widen the listing once the first load has settled.
  useEffect(() => {
    if (status === 'success' && !filters.includeDescendants) setFilters({ ...filters, includeDescendants: true });
  }, [status, filters, setFilters]);

  const ids = useMemo(() => users.map((u) => u.id), [users]);
  const { counts, invalidate } = useSshKeyCounts(ids);
  const [expanded, setExpanded] = useState<string | null>(null);

  const orgName = (tenantId: string) => tenants.find((t) => t.id === tenantId)?.name ?? tenantId;
  const permits = (u: TenantUserResponse) =>
    u.role_capabilities?.sftp_access === true || u.role_capabilities?.full_access === true;
  const count = (id: string) => {
    const n = counts[id];
    return n === undefined ? '…' : n === null ? '?' : String(n);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <input
          data-testid="sftp-accounts-search"
          placeholder="Search users…"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          style={{ ...inputStyle, width: 260 }}
        />
        <span style={{ fontSize: 11, color: 'var(--text-soft)' }}>
          Users across your organization tree. ✓ / ✗ = the role carries SFTP access. Manage… opens the same key list as
          My keys for that user.
        </span>
      </div>
      {status === 'error' && (
        <div role="alert" style={{ fontSize: 12, color: 'var(--kind-failure)', marginBottom: 8 }}>
          {error}
        </div>
      )}
      {status === 'success' && users.length === 0 ? (
        <Empty>No users match.</Empty>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr style={theadRowStyle}>
              <Th first>User</Th>
              <Th>Org</Th>
              <Th>Role · SFTP</Th>
              <Th>Keys</Th>
              <Th>{' '}</Th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <Fragment key={u.id}>
                <tr data-testid={`sftp-accounts-row-${u.id}`} style={{ borderTop: '1px solid var(--border)' }}>
                  <Td first>
                    <div style={{ fontWeight: 500 }}>{u.display_name ?? u.email}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>{u.email}</div>
                  </Td>
                  <Td>{orgName(u.tenant_id)}</Td>
                  <Td>
                    {u.role_slug ?? '—'} <span title={permits(u) ? 'role carries SFTP access' : 'role has no SFTP access'}>{permits(u) ? '✓' : '✗'}</span>
                  </Td>
                  <Td>{count(u.id)}</Td>
                  <Td>
                    <button
                      type="button"
                      data-testid={`sftp-accounts-manage-${u.id}`}
                      style={{ ...secondaryButton, padding: '3px 8px', fontSize: 11 }}
                      onClick={() => setExpanded(expanded === u.id ? null : u.id)}
                    >
                      {expanded === u.id ? 'Close' : 'Manage…'}
                    </button>
                  </Td>
                </tr>
                {expanded === u.id && (
                  <tr>
                    <td colSpan={5} style={{ padding: '4px 20px 16px', background: 'var(--bg-alt)' }}>
                      <SshKeyList userId={u.id} userLabel={u.email} canManage variant="table" onChanged={() => invalidate(u.id)} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Allowlist ─────────────────────────────────────────────────────────────────

function AllowlistPage({ settings, onSaved }: { settings: ReturnType<typeof useSftpSettings>; onSaved: () => void }) {
  const current = settings.settings;
  const [draft, setDraft] = useState<SftpSettingsBody | null>(null);
  const [cidr, setCidr] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Re-seed the draft whenever the server copy changes (initial load, after a save).
  useEffect(() => {
    if (current) setDraft({ enabled: current.enabled, allowed_cidrs: [...current.allowed_cidrs], allow_any: current.allow_any });
  }, [current]);

  if (settings.status === 'error') {
    return (
      <div role="alert" style={{ fontSize: 12, color: 'var(--kind-failure)' }}>
        {settings.error}
      </div>
    );
  }
  if (!draft || !current) return <div style={{ fontSize: 12, color: 'var(--text-mute)' }}>Loading settings…</div>;

  const dirty =
    draft.enabled !== current.enabled ||
    draft.allow_any !== current.allow_any ||
    draft.allowed_cidrs.join(',') !== current.allowed_cidrs.join(',');
  const lockedOut = draft.enabled && !draft.allow_any && draft.allowed_cidrs.length === 0;

  const addCidr = () => {
    const v = cidr.trim();
    if (!v) return;
    if (!draft.allowed_cidrs.includes(v)) setDraft({ ...draft, allowed_cidrs: [...draft.allowed_cidrs, v] });
    setCidr('');
    setSaved(false);
  };
  const removeCidr = (v: string) => {
    setDraft({ ...draft, allowed_cidrs: draft.allowed_cidrs.filter((c) => c !== v) });
    setSaved(false);
  };
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await settings.save(draft);
      setSaved(true);
      onSaved(); // the Connection card's access verdict (sftp_disabled) follows the switch
    } catch (err) {
      setError(describeApiError(err, 'The settings were not saved'));
    } finally {
      setBusy(false);
    }
  };

  const radio = (value: boolean, label: string, testId: string) => (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text)', cursor: 'pointer' }}>
      <input
        type="radio"
        name="sftp-enabled"
        data-testid={testId}
        checked={draft.enabled === value}
        onChange={() => {
          setDraft({ ...draft, enabled: value });
          setSaved(false);
        }}
        style={{ accentColor: 'var(--accent)' }}
      />
      {label}
    </label>
  );

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>SFTP server</span>
        {radio(true, 'Enabled', 'sftp-allow-enabled')}
        {radio(false, 'Disabled', 'sftp-allow-disabled')}
        <span style={{ fontSize: 11, color: 'var(--text-soft)' }}>Disabling ends every live session within 30 seconds.</span>
      </div>

      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Source IP allowlist</div>
      <div style={{ fontSize: 11, color: 'var(--text-soft)', marginBottom: 10, lineHeight: 1.5 }}>
        Applies to the next connection within 30 seconds — no deployment needed. Loopback (kubectl port-forward) is
        always admitted, so cluster RBAC stays the break-glass. Never add cluster-private ranges.
      </div>

      {draft.allowed_cidrs.length === 0 ? (
        <Empty>No CIDRs on the allowlist.</Empty>
      ) : (
        <table style={{ ...tableStyle, marginBottom: 10 }}>
          <thead>
            <tr style={theadRowStyle}>
              <Th first>CIDR</Th>
              <Th>{' '}</Th>
            </tr>
          </thead>
          <tbody>
            {draft.allowed_cidrs.map((c) => (
              <tr key={c} data-testid={`sftp-allow-row-${c}`} style={{ borderTop: '1px solid var(--border)' }}>
                <Td first isMono>
                  {c}
                </Td>
                <Td>
                  <button
                    type="button"
                    data-testid={`sftp-allow-remove-${c}`}
                    style={{ ...secondaryButton, padding: '3px 8px', fontSize: 11 }}
                    onClick={() => removeCidr(c)}
                  >
                    Remove
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <input
          data-testid="sftp-allow-cidr-input"
          value={cidr}
          onChange={(e) => setCidr(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') addCidr();
          }}
          placeholder="203.0.113.7/32 or 2001:db8::/32"
          style={{ ...inputStyle, width: 280, fontFamily: mono }}
        />
        <button type="button" data-testid="sftp-allow-add" style={secondaryButton} onClick={addCidr} disabled={!cidr.trim()}>
          Add
        </button>
      </div>

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: 'var(--text)', marginBottom: 14, cursor: 'pointer' }}>
        <input
          type="checkbox"
          data-testid="sftp-allow-any"
          checked={draft.allow_any}
          onChange={(e) => {
            setDraft({ ...draft, allow_any: e.target.checked });
            setSaved(false);
          }}
          style={{ marginTop: 2, accentColor: 'var(--accent)' }}
        />
        <span>
          Allow any source IP
          <span style={{ color: 'var(--text-soft)' }}> — ⚠ disables the allowlist; key authentication is still required.</span>
        </span>
      </label>

      {lockedOut && (
        <div style={{ marginBottom: 12 }}>
          <Pill tone="warn">no CIDRs and “allow any” off — only kubectl port-forward can connect</Pill>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          type="button"
          data-testid="sftp-allow-save"
          style={{ ...primaryButton, opacity: dirty && !busy ? 1 : 0.5 }}
          onClick={() => void save()}
          disabled={!dirty || busy}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        {saved && !dirty && <span style={{ fontSize: 11, color: '#2f855a' }}>Saved — live within 30 seconds.</span>}
        {current.updated_at && (
          <span style={{ fontSize: 11, color: 'var(--text-mute)' }}>
            Last changed {fmtWhen(current.updated_at)}
            {current.updated_by ? ` by ${current.updated_by}` : ''}
          </span>
        )}
      </div>
      {error && (
        <div data-testid="sftp-allow-error" role="alert" style={{ fontSize: 12, color: 'var(--kind-failure)', marginTop: 10 }}>
          {error}
        </div>
      )}
    </div>
  );
}

// ── Connection ────────────────────────────────────────────────────────────────

function CopyButton({ text, testId }: { text: string | null; testId: string }) {
  const [copied, setCopied] = useState(false);
  // Nothing to copy still occupies its grid cell — otherwise the rows below shift a column.
  if (!text) return <span />;
  return (
    <button
      type="button"
      data-testid={testId}
      style={{ ...secondaryButton, padding: '3px 8px', fontSize: 11 }}
      onClick={() => {
        void navigator.clipboard?.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function ConnectionPage({ conn }: { conn: ReturnType<typeof useSftpConnection> }) {
  const c = conn.connection;
  if (conn.status === 'error') {
    return (
      <div data-testid="sftp-conn-error" role="alert" style={{ fontSize: 12, color: 'var(--kind-failure)' }}>
        {conn.error}
      </div>
    );
  }
  if (!c) return <div style={{ fontSize: 12, color: 'var(--text-mute)' }}>Loading connection details…</div>;

  const rows: Array<{ label: string; value: React.ReactNode; copy: string | null; testId: string }> = [
    {
      label: 'Host',
      value: c.host ?? <span style={{ color: 'var(--text-mute)' }}>not configured (SFTP_PUBLIC_HOST)</span>,
      copy: c.host,
      testId: 'sftp-conn-host',
    },
    { label: 'Port', value: String(c.port), copy: String(c.port), testId: 'sftp-conn-port' },
    { label: 'Username', value: c.username ?? '—', copy: c.username, testId: 'sftp-conn-username' },
    {
      label: 'Host key',
      value: c.host_key_fingerprint ? `${c.host_key_fingerprint} (ed25519)` : <span style={{ color: 'var(--text-mute)' }}>unknown until the server heartbeats</span>,
      copy: c.host_key_fingerprint,
      testId: 'sftp-conn-hostkey',
    },
  ];
  const access = c.access.permitted
    ? `✓ your role carries SFTP access · ${c.access.visible_tenant_count} tenant folder${c.access.visible_tenant_count === 1 ? '' : 's'} visible`
    : ACCESS_REASON[c.access.reason ?? 'no_capability'];

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr max-content', gap: '8px 16px', alignItems: 'center', fontSize: 12 }}>
        {rows.map((r) => (
          <Fragment key={r.label}>
            <div style={{ color: 'var(--text-soft)' }}>{r.label}</div>
            <div data-testid={r.testId} style={{ color: 'var(--text)', fontFamily: mono, fontSize: 11, wordBreak: 'break-all' }}>
              {r.value}
            </div>
            <CopyButton text={r.copy} testId={`${r.testId}-copy`} />
          </Fragment>
        ))}
        <div style={{ color: 'var(--text-soft)' }}>Your access</div>
        <div data-testid="sftp-conn-access" style={{ color: c.access.permitted ? 'var(--text)' : 'var(--kind-failure)' }}>
          {access}
        </div>
        <div />
      </div>

      <div style={{ marginTop: 18, fontSize: 11, color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
        Command
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 6 }}>
        <pre
          data-testid="sftp-conn-command"
          style={{
            flex: 1,
            margin: 0,
            padding: '10px 12px',
            background: 'var(--bg-alt)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            fontFamily: mono,
            fontSize: 11,
            color: 'var(--text)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {c.command ?? '— (host not configured)'}
        </pre>
        <CopyButton text={c.command} testId="sftp-conn-command-copy" />
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-mute)', marginTop: 10, lineHeight: 1.6 }}>
        The quotes matter — your username contains an @. On first connect compare the fingerprint your client prints
        with the host key above. FileZilla / WinSCP: protocol SFTP, port {c.port}, key-file authentication, username =
        your email.
        <br />
        Fallback without an allowlisted IP (cluster access required):{' '}
        <span style={{ fontFamily: mono }}>kubectl port-forward svc/agent-sftp-server 2222:22</span>, then{' '}
        <span style={{ fontFamily: mono }}>sftp -P 2222 -i ~/.ssh/&lt;your-key&gt; '{c.username ?? 'you@example.com'}'@127.0.0.1</span>.
      </div>
    </div>
  );
}
